import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NzAutocompleteComponent,
  NzAutocompleteTriggerDirective,
} from 'ng-zorro-antd/auto-complete';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { TranslatePipe } from '../../pipes/translate.pipe';

/**
 * Shared server-side search input.
 *
 * Renders a clearable ng-zorro input (search prefix + clear suffix) and emits
 * the term through `(search)` **debounced** so typing doesn't flood the API.
 * Drop it into any `DataTableComponent` page:
 *
 * ```html
 * <app-search-input [value]="search" (search)="onSearch($event)"></app-search-input>
 * ```
 *
 * Pass `[autocomplete]` an `<nz-autocomplete>` to hang a suggestion panel off
 * the same field — see `fabric-search`.
 */
@Component({
  selector: 'app-search-input',
  standalone: true,
  imports: [
    FormsModule,
    NzInputModule,
    NzIconModule,
    NzAutocompleteTriggerDirective,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nz-input-group class="search-input" [nzPrefix]="prefixTpl" [nzSuffix]="suffixTpl">
      <!-- One input, written twice: a directive cannot be applied conditionally, and
           nzAutocomplete's trigger throws on focus if the attribute is there without a
           panel — so the plain branch must not carry it at all. Keep the two in step. -->
      @if (autocomplete) {
        <input
          #field
          nz-input
          type="search"
          [nzAutocomplete]="autocomplete"
          [placeholder]="placeholder | t"
          [ngModel]="term()"
          (ngModelChange)="onInput($event)"
          [attr.aria-label]="(ariaLabel || placeholder) | t"
        />
      } @else {
        <input
          #field
          nz-input
          type="search"
          [placeholder]="placeholder | t"
          [ngModel]="term()"
          (ngModelChange)="onInput($event)"
          [attr.aria-label]="(ariaLabel || placeholder) | t"
        />
      }
    </nz-input-group>
    <ng-template #prefixTpl><span nz-icon nzType="search"></span></ng-template>
    <ng-template #suffixTpl>
      @if (term()) {
        <button
          type="button"
          class="search-input__clear"
          (click)="clear()"
          [attr.aria-label]="'Clear search' | t"
        >
          <span nz-icon nzType="close-circle" nzTheme="fill"></span>
        </button>
      }
    </ng-template>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .search-input {
        width: 100%;
      }

      :host ::ng-deep {
        .ant-input-affix-wrapper {
          height: 44px;
          align-items: center;
          border-radius: 999px;
          border-color: #ececec;
          padding-inline: 1rem 0.75rem;
          transition: border-color 0.2s ease;
        }
        .ant-input-affix-wrapper:hover {
          border-color: #c4c4c4;
        }
        .ant-input-affix-wrapper:focus-within,
        .ant-input-affix-wrapper-focused {
          border-color: #0a0a0a;
          box-shadow: none;
        }
        .ant-input-prefix {
          margin-inline-end: 0.6rem;
          color: #6b6b6b;
        }
        .ant-input {
          font-size: 0.9rem;
          color: #1f1f1f;
        }
        // hide the browser's native search clear — we render our own
        .ant-input[type='search']::-webkit-search-cancel-button {
          appearance: none;
        }
      }

      .search-input__clear {
        display: inline-flex;
        padding: 0;
        border: 0;
        background: none;
        color: #6b6b6b;
        cursor: pointer;
        transition: color 0.2s ease;

        &:hover {
          color: #0a0a0a;
        }
      }
    `,
  ],
})
export class SearchInputComponent implements OnInit, OnDestroy {
  /** Whichever of the two branches is rendered — only one ever is. */
  private readonly field = viewChild<ElementRef<HTMLInputElement>>('field');

  /** Initial / externally-controlled term (e.g. a persisted search). */
  @Input() set value(v: string | null | undefined) {
    const next = v ?? '';
    this.term.set(next);
    this.lastEmitted = next.trim();
  }

  /** Placeholder translation key. */
  @Input() placeholder = 'Search…';
  /** Aria-label translation key (falls back to the placeholder). */
  @Input() ariaLabel = '';
  /** Debounce window in ms before `(search)` fires. */
  @Input() debounce = 350;

  /**
   * Suggestion panel to open under the field. Left null the input is an ordinary
   * search box; the trigger directive is only attached when a panel is given.
   */
  @Input() autocomplete: NzAutocompleteComponent | null = null;

  /** Emits the trimmed term, debounced; fires immediately on clear. */
  @Output() readonly search = new EventEmitter<string>();

  readonly term = signal('');

  private lastEmitted = '';
  private readonly input$ = new Subject<string>();
  private sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.input$.pipe(debounceTime(this.debounce)).subscribe((v) => this.emit(v));
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** For a field revealed by a button: the caret belongs in it, not where the button was. */
  focus(): void {
    this.field()?.nativeElement.focus();
  }

  onInput(value: string): void {
    this.term.set(value);
    this.input$.next(value);
  }

  clear(): void {
    this.term.set('');
    // Replace any pending debounced term, then emit the empty result instantly.
    this.input$.next('');
    this.emit('');
  }

  private emit(value: string): void {
    const trimmed = value.trim();
    if (trimmed === this.lastEmitted) return;
    this.lastEmitted = trimmed;
    this.search.emit(trimmed);
  }
}
